import { toast } from 'sonner';
import { QueryClient, MutationCache, QueryCache } from '@tanstack/react-query';
import { getErrorToastContent } from '../../utils/error-toast';
import { categorizeError, ErrorType } from '../../utils/error-utils';
import { createRetryFunction, createRetryDelayFunction } from '../utils/retry';

// Global error handler for queries
export const queryCache = new QueryCache({
  onError: (error, query) => {
    const appError = categorizeError(error);
    if (appError.type === ErrorType.AUTHENTICATION) {
      return;
    }

    // 402 (no subscription / quota) is a business state handled by route guards,
    // not an unexpected error — don't show error toasts for it.
    if (appError.statusCode === 402) {
      return;
    }

    // Only show toast for user-facing queries (not background refetches)
    if (query.state.dataUpdateCount === 0) {
      const { title, message } = getErrorToastContent(error);
      toast.error(title, { description: message });
    }
  },
});

// Global error handler for mutations
export const mutationCache = new MutationCache({
  onError: (error, _variables, _context, mutation) => {
    if (mutation.options.onError) {
      return;
    }

    const { title, message } = getErrorToastContent(error);
    toast.error(title, { description: message });
  },
});

/**
 * Create a QueryClient instance with sensible defaults
 * for the Coldsea Deploy application
 */
export const createQueryClient = () =>
  new QueryClient({
    queryCache,
    mutationCache,
    defaultOptions: {
      queries: {
        // How long before data is considered stale
        staleTime: 5 * 60 * 1000, // 5 minutes

        // How long to keep unused data in cache
        gcTime: 10 * 60 * 1000, // 10 minutes (previously cacheTime)

        // Enhanced retry configuration with smart error handling
        retry: createRetryFunction(true),

        // Enhanced retry delay with exponential backoff and jitter
        retryDelay: createRetryDelayFunction(),

        // Refetch on window focus (good for keeping data fresh)
        refetchOnWindowFocus: true,

        // Always refetch on reconnect to ensure fresh data
        refetchOnReconnect: 'always',

        // Network mode - continue showing cached data when offline
        networkMode: 'offlineFirst',

        // Use error boundaries for unhandled errors
        throwOnError: (error) => {
          const appError = categorizeError(error);
          // Don't throw AUTH errors - they should be handled by redirects, not error boundaries
          // Throwing auth errors causes infinite loops when components fetch before auth completes
          // Only throw SERVER and UNKNOWN errors to error boundaries
          return appError.type === ErrorType.SERVER || appError.type === ErrorType.UNKNOWN;
        },
      },
      mutations: {
        // Conservative retry for mutations
        retry: createRetryFunction(false),

        // Enhanced retry delay for mutations
        retryDelay: createRetryDelayFunction(),

        // Network mode - pause mutations when offline and persist them
        networkMode: 'offlineFirst',

        // Use error boundaries for unhandled errors
        throwOnError: (error) => {
          const appError = categorizeError(error);
          // Don't throw AUTH errors - they should be handled by redirects, not error boundaries
          // Throwing auth errors causes infinite loops when components fetch before auth completes
          // Only throw SERVER and UNKNOWN errors to error boundaries
          return appError.type === ErrorType.SERVER || appError.type === ErrorType.UNKNOWN;
        },
      },
    },
  });
